import * as THREE from "three";
import { getIkTargetActions, getIkTargetState, useIkTargetStore } from "../../stores/ikTargetStore";
import { logger } from "../../utils/logger";

const IK_MARKER_NAME = "__ikTargetMarker";

export type IkMarkerWorld = [number, number, number];
export type IkSceneRef = { current?: THREE.Scene | null } | null | undefined;
type MaybeScene = THREE.Scene | null | undefined;
type MaybeWorld = IkMarkerWorld | null | undefined;

export function ensureIkTargetMarker(scene: MaybeScene): THREE.Object3D | null {
  if (!scene) return null;
  let marker = scene.getObjectByName(IK_MARKER_NAME);
  if (!marker) {
    const geometry = new THREE.SphereGeometry(0.03, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00bfff,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.9,
    });
    marker = new THREE.Mesh(geometry, material);
    marker.name = IK_MARKER_NAME;
    marker.renderOrder = 9999;
    marker.visible = false;
    scene.add(marker);
  }
  try {
    marker.userData = {
      ...(marker.userData || {}),
      isIkTargetMarker: true,
      type: "ikTargetMarker",
    };
  } catch {
    // ignore
  }
  return marker;
}

export function removeIkTargetMarker(scene: MaybeScene): void {
  if (!scene) return;
  const marker = scene.getObjectByName(IK_MARKER_NAME) as THREE.Mesh | null;
  if (marker) {
    try {
      marker.parent?.remove(marker);
    } catch {
      // ignore
    }
    try {
      marker.geometry.dispose();
      const mat = marker.material;
      if (mat) {
        if (Array.isArray(mat)) {
          mat.forEach((m) => m?.dispose && m.dispose());
        } else if (typeof mat.dispose === "function") {
          mat.dispose();
        }
      }
    } catch (e) {
      logger.warn("[Scene] Failed to dispose IK marker resources", e);
    }
  }
  getIkTargetActions().clearMarkerWorld();
}

export function setIkTargetMarker(scene: MaybeScene, world: MaybeWorld): void {
  if (!scene) return;
  if (!Array.isArray(world) || world.length < 3) return;
  try {
    const marker = ensureIkTargetMarker(scene) as THREE.Mesh | null;
    if (!marker) return;
    marker.position.set(Number(world[0]) || 0, Number(world[1]) || 0, Number(world[2]) || 0);
    const visible = getIkTargetState().markerVisible === true;
    marker.visible = visible;
  } catch (error) {
    logger.warn("[Scene] Unable to show IK target marker from event", error);
  }
}

export function setupIkTargetMarkerBridge(sceneRef: IkSceneRef): () => void {
  const renderFromStore = () => {
    const scene = sceneRef?.current;
    if (!scene) return;
    const state = getIkTargetState();
    const world = state.markerWorld;
    if (Array.isArray(world) && world.length >= 3) {
      setIkTargetMarker(scene, world);
      return;
    }
    const marker = scene.getObjectByName(IK_MARKER_NAME);
    if (marker) marker.visible = false;
  };

  const ikPickedHandler = (event: Event) => {
    const world = (event as CustomEvent<{ world?: MaybeWorld }>).detail?.world;
    getIkTargetActions().setMarkerWorld(Array.isArray(world) && world.length >= 3 ? world : null);
  };

  const ikClearHandler = () => {
    try {
      removeIkTargetMarker(sceneRef?.current);
    } catch (error) {
      logger.warn("[Scene] Unable to clear IK target marker", error);
    }
  };

  const ikMarkerVisibilityHandler = (event: Event) => {
    const visible = (event as CustomEvent<{ visible?: boolean }>).detail?.visible !== false;
    getIkTargetActions().setMarkerVisible(visible);
    try {
      const scene = sceneRef?.current;
      if (!scene) return;
      const marker = scene.getObjectByName(IK_MARKER_NAME);
      if (marker) {
        marker.visible = visible;
      } else if (visible) {
        const cached = getIkTargetState().markerWorld;
        if (cached) setIkTargetMarker(scene, cached);
      }
    } catch (error) {
      logger.warn("[Scene] Unable to apply IK marker visibility", error);
    }
  };

  let prevState = getIkTargetState();
  const unsubscribeStore = useIkTargetStore.subscribe((state) => {
    const nextMarkerWorld = state.markerWorld;
    const nextMarkerVisible = state.markerVisible;
    const markerWorldChanged = nextMarkerWorld !== prevState.markerWorld;
    const markerVisibleChanged = nextMarkerVisible !== prevState.markerVisible;
    prevState = state;
    if (markerWorldChanged) {
      renderFromStore();
      return;
    }
    if (!markerVisibleChanged) return;
    try {
      const scene = sceneRef?.current;
      if (!scene) return;
      const marker = scene.getObjectByName(IK_MARKER_NAME);
      if (marker) {
        marker.visible = nextMarkerVisible;
      } else if (nextMarkerVisible) {
        renderFromStore();
      }
    } catch (error) {
      logger.warn("[Scene] Unable to apply IK marker visibility", error);
    }
  });
  renderFromStore();

  window.addEventListener("ik-target-picked", ikPickedHandler);
  window.addEventListener("ik-target-clear", ikClearHandler);
  window.addEventListener("ik-target-marker-visibility", ikMarkerVisibilityHandler);

  return () => {
    unsubscribeStore();
    window.removeEventListener("ik-target-picked", ikPickedHandler);
    window.removeEventListener("ik-target-clear", ikClearHandler);
    window.removeEventListener("ik-target-marker-visibility", ikMarkerVisibilityHandler);
  };
}
